"use client";

import React, { useState, useEffect, useRef, createContext, useContext, ReactNode } from 'react';
import { usePathname } from 'next/navigation';

const AUTH_CACHE_MS = 30_000;

interface User {
  id: string;
  email: string;
  name?: string;
  phone?: string;
  image?: string;
  role?: string;
  department?: string;
  notification_email?: boolean;
  notification_push?: boolean;
  notification_marketing?: boolean;
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const pathname = usePathname();
  const lastFetchAtRef = useRef<number>(0);

  const fetchUser = async () => {
    try {
      const response = await fetch('/api/auth/me');
      const data = await response.json();

      if (data.success) {
        setUser(data.user);
      } else {
        setUser(null);
      }
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  };

  const refreshUser = async () => {
    lastFetchAtRef.current = Date.now();
    await fetchUser();
  };

  const login = async (email: string, password: string) => {
    const response = await fetch('/api/auth/login', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password }),
    });

    const data = await response.json();

    if (data.success) {
      setUser(data.user);
      // Force complete window reload
      setTimeout(() => {
        window.location.reload();
      }, 500);
    } else {
      throw new Error(data.error || 'Login failed');
    }
  };

  const register = async (email: string, password: string, name?: string) => {
    const response = await fetch('/api/auth/register', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email, password, name }),
    });

    const data = await response.json();

    if (data.success) {
      setUser(data.user);
      // Don't reload here - let component handle it
    } else {
      throw new Error(data.error || 'Registration failed');
    }
  };

  const logout = async () => {
    await fetch('/api/auth/logout', { method: 'POST' });
    setUser(null);
    // Force page reload to ensure all components refresh properly
    window.location.reload();
  };

  useEffect(() => {
    const authPages = ['/sign-in', '/sign-up', '/forgot-password', '/reset-password'];
    if (authPages.includes(pathname)) {
      setUser(null);
      setLoading(false);
      return;
    }

    const now = Date.now();
    if (lastFetchAtRef.current > 0 && now - lastFetchAtRef.current < AUTH_CACHE_MS) {
      setLoading(false);
      return;
    }
    lastFetchAtRef.current = now;
    fetchUser();
  }, [pathname]);

  const contextValue: AuthContextType = {
    user,
    loading,
    login,
    register,
    logout,
    refreshUser
  };

  return (
    <AuthContext.Provider value={contextValue}>
      {children}
    </AuthContext.Provider>
  );
};
